// ShareYourWall.js — public volunteer photo submission page
// Route: /share-your-wall (public, no auth guard)
// Writes: Firestore collection "validationSubmissions" + Storage under validation-submissions/<docId>/
//
// NOTE: Uses Firebase v9 modular API. If firebase.js exports the v8 namespaced API,
// the db/storage calls need swapping (ask Claude for the v8 variant).

import React, { useRef, useState } from "react";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytes } from "firebase/storage";
import { db, storage } from "../services/firebase";
import "./ShareYourWall.css";

// ---- Capture guardrail threshold ----
// Matches CAPTURE_MIN_DIM in TapToMeasure.js: the photo's shortest edge must be
// at least this many pixels or it's too low-res to measure from.
// (TapToMeasure's warn tier is PPI-based and can't run pre-calibration, so it
// fires later, when the photo is processed in Surface Mode.)
const SYW_CAPTURE_MIN_DIM = 1200;

// Public URL for the printable AI Stick (confirmed downloadable from prod without sign-in)
const AI_STICK_URL = "https://www.measuresmart.ai/ai-stick/ai-stick-v2-prod.pdf";

const ftInToInches = (ft, inch) => {
  const f = parseFloat(ft) || 0;
  const i = parseFloat(inch) || 0;
  return f * 12 + i;
};

// Reads image dimensions from a File without uploading it
const readImageDims = (file) =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const dims = { w: img.naturalWidth, h: img.naturalHeight };
      URL.revokeObjectURL(url);
      resolve(dims);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    img.src = url;
  });

const classifyPhoto = (dims) => {
  if (!dims) return { tier: "block", reason: "That file doesn't look like a photo. Please choose an image." };
  const minDim = Math.min(dims.w, dims.h);
  if (minDim < SYW_CAPTURE_MIN_DIM) {
    return {
      tier: "block",
      reason:
        "This photo is too low-resolution to measure from. If you're uploading from a computer, make sure the photo wasn't emailed or texted at reduced size — transfer the original. If you're on your phone, retake with the regular camera.",
    };
  }
  return { tier: "pass", reason: "" };
};

export default function ShareYourWall() {
  const [wallPhotos, setWallPhotos] = useState([]); // [{ file, tier, reason, previewUrl }]
  const [tapePhoto, setTapePhoto] = useState(null); // { file, tier, reason, previewUrl }
  const [widthFt, setWidthFt] = useState("");
  const [widthIn, setWidthIn] = useState("");
  const [heightFt, setHeightFt] = useState("");
  const [heightIn, setHeightIn] = useState("");
  const [name, setName] = useState("");
  const [contact, setContact] = useState("");
  const [note, setNote] = useState("");
  const [anonymous, setAnonymous] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [viewerUrl, setViewerUrl] = useState(null);

  const wallInputRef = useRef(null);
  const tapeInputRef = useRef(null);

  const handleWallFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const evaluated = [];
    for (const file of files) {
      const dims = await readImageDims(file);
      const verdict = classifyPhoto(dims);
      evaluated.push({
        file,
        dims,
        tier: verdict.tier,
        reason: verdict.reason,
        previewUrl: URL.createObjectURL(file),
      });
    }
    setWallPhotos((prev) => [...prev, ...evaluated]);
    if (wallInputRef.current) wallInputRef.current.value = "";
  };

  const handleTapeFile = async (e) => {
    const file = (e.target.files || [])[0];
    if (!file) return;
    // Tape photo only needs to be readable, not measurement-grade — no block tier.
    setTapePhoto({ file, tier: "pass", reason: "", previewUrl: URL.createObjectURL(file) });
    if (tapeInputRef.current) tapeInputRef.current.value = "";
  };

  const removeWallPhoto = (idx) => {
    setWallPhotos((prev) => {
      URL.revokeObjectURL(prev[idx].previewUrl);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const usableWallPhotos = wallPhotos.filter((p) => p.tier !== "block");
  const actualWidthIn = ftInToInches(widthFt, widthIn);
  const actualHeightIn = ftInToInches(heightFt, heightIn);

  const canSubmit =
    usableWallPhotos.length > 0 &&
    tapePhoto &&
    actualWidthIn > 0 &&
    actualHeightIn > 0 &&
    (anonymous || name.trim().length > 0) &&
    contact.trim().length > 0 &&
    !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError("");
    try {
      const docRef = await addDoc(collection(db, "validationSubmissions"), {
        status: "queued",
        submitterType: "volunteer",
        name: anonymous ? "" : name.trim(),
        anonymous,
        contact: contact.trim(),
        note: note.trim(),
        actualWidthIn,
        actualHeightIn,
        wallPhotoCount: usableWallPhotos.length,
        userAgent: navigator.userAgent,
        createdAt: serverTimestamp(),
      });

      const base = `validation-submissions/${docRef.id}`;
      await Promise.all([
        ...usableWallPhotos.map((p, i) =>
          uploadBytes(ref(storage, `${base}/wall-${i + 1}-${p.file.name}`), p.file)
        ),
        uploadBytes(ref(storage, `${base}/tape-${tapePhoto.file.name}`), tapePhoto.file),
      ]);

      setSubmitted(true);
    } catch (err) {
      console.error("share-your-wall submit failed", err);
      setError(
        "Something went wrong sending your photos. Please try again — and if it keeps failing, message Ernie directly."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="syw-page">
        <div className="syw-card syw-thanks">
          <div className="syw-thanks-check">✓</div>
          <h1>Got it — thank you!</h1>
          <p>
            Your wall is in the queue. We'll process your photos through MeasureSmart and compare
            the reading to your tape measurement.
          </p>
          <p className="syw-thanks-sub">
            Results are published weekly on our Facebook page — watch for your wall and your
            official data point number when it joins the reliability record.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="syw-page">
      <header className="syw-header">
        <h1>Share your wall</h1>
        <p>
          Help us test MeasureSmart against real tape measurements. Upload your photos and your
          measurement below — we do the rest and send you the results.
        </p>
        <p className="syw-stick-line">
          Haven't printed the AI Stick yet?{" "}
          <a href={AI_STICK_URL} target="_blank" rel="noopener noreferrer">
            Download it here
          </a>{" "}
          — and check the print: the stick should measure exactly 11 inches.
        </p>
      </header>

      {/* Wall photos */}
      <section className="syw-card">
        <h2>1. Wall photos (with the AI Stick on the wall)</h2>
        <p className="syw-hint">Two or three shots from slightly different distances are ideal.</p>
        <button className="syw-file-btn" onClick={() => wallInputRef.current?.click()}>
          Add wall photo(s)
        </button>
        <input
          ref={wallInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleWallFiles}
          style={{ display: "none" }}
        />
        {wallPhotos.length > 0 && (
          <ul className="syw-photo-list">
            {wallPhotos.map((p, i) => (
              <li key={i} className={`syw-photo-item syw-tier-${p.tier}`}>
                <img
                  src={p.previewUrl}
                  alt={`Wall photo ${i + 1} — tap to view full size`}
                  onClick={() => setViewerUrl(p.previewUrl)}
                />
                <div className="syw-photo-meta">
                  {p.tier === "block" && <span className="syw-badge syw-badge-block">Won't work</span>}
                  {p.tier === "warn" && <span className="syw-badge syw-badge-warn">Could be better</span>}
                  {p.tier === "pass" && <span className="syw-badge syw-badge-pass">Looks good</span>}
                  {p.reason && <p className="syw-reason">{p.reason}</p>}
                </div>
                <button
                  className="syw-remove"
                  onClick={() => removeWallPhoto(i)}
                  aria-label="Remove photo"
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Tape measure photo */}
      <section className="syw-card">
        <h2>2. Tape measure photo</h2>
        <p className="syw-hint">
          A photo of your tape measure held against the wall, numbers readable. This is our proof of
          the true size.
        </p>
        <button className="syw-file-btn" onClick={() => tapeInputRef.current?.click()}>
          {tapePhoto ? "Replace tape photo" : "Add tape photo"}
        </button>
        <input
          ref={tapeInputRef}
          type="file"
          accept="image/*"
          onChange={handleTapeFile}
          style={{ display: "none" }}
        />
        {tapePhoto && (
          <ul className="syw-photo-list">
            <li className="syw-photo-item syw-tier-pass">
              <img
                src={tapePhoto.previewUrl}
                alt="Tape measure — tap to view full size"
                onClick={() => setViewerUrl(tapePhoto.previewUrl)}
              />
              <div className="syw-photo-meta">
                <span className="syw-badge syw-badge-pass">Attached</span>
              </div>
            </li>
          </ul>
        )}
      </section>

      {/* Actual measurements */}
      <section className="syw-card">
        <h2>3. Your tape measurement</h2>
        <div className="syw-dims">
          <fieldset className="syw-dim">
            <legend>Width</legend>
            <input
              type="number"
              inputMode="numeric"
              min="0"
              placeholder="ft"
              value={widthFt}
              onChange={(e) => setWidthFt(e.target.value)}
              aria-label="Width feet"
            />
            <span className="syw-unit">ft</span>
            <input
              type="number"
              inputMode="decimal"
              min="0"
              max="11.99"
              step="0.25"
              placeholder="in"
              value={widthIn}
              onChange={(e) => setWidthIn(e.target.value)}
              aria-label="Width inches"
            />
            <span className="syw-unit">in</span>
          </fieldset>
          <fieldset className="syw-dim">
            <legend>Height</legend>
            <input
              type="number"
              inputMode="numeric"
              min="0"
              placeholder="ft"
              value={heightFt}
              onChange={(e) => setHeightFt(e.target.value)}
              aria-label="Height feet"
            />
            <span className="syw-unit">ft</span>
            <input
              type="number"
              inputMode="decimal"
              min="0"
              max="11.99"
              step="0.25"
              placeholder="in"
              value={heightIn}
              onChange={(e) => setHeightIn(e.target.value)}
              aria-label="Height inches"
            />
            <span className="syw-unit">in</span>
          </fieldset>
        </div>
      </section>

      {/* Contact */}
      <section className="syw-card">
        <h2>4. About you</h2>
        <div className="syw-field">
          <label htmlFor="syw-name">First name</label>
          <input
            id="syw-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={anonymous}
            placeholder={anonymous ? "Anonymous" : "e.g., Maria"}
          />
        </div>
        <label className="syw-check">
          <input
            type="checkbox"
            checked={anonymous}
            onChange={(e) => setAnonymous(e.target.checked)}
          />
          List my wall anonymously on the reliability record
        </label>
        <div className="syw-field">
          <label htmlFor="syw-contact">Phone or email (so we can send your results)</label>
          <input
            id="syw-contact"
            type="text"
            value={contact}
            onChange={(e) => setContact(e.target.value)}
            placeholder="you@example.com or (201) 555-0100"
          />
        </div>
        <div className="syw-field">
          <label htmlFor="syw-note">Anything we should know? (optional)</label>
          <textarea
            id="syw-note"
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g., wall has wainscoting below 36 inches"
          />
        </div>
      </section>

      {error && <p className="syw-error" role="alert">{error}</p>}

      <button className="syw-submit" onClick={handleSubmit} disabled={!canSubmit}>
        {submitting ? "Sending your photos…" : "Send it in"}
      </button>
      <p className="syw-footnote">
        Photos are used only for measurement validation. Nothing is published without your OK.
      </p>

      {viewerUrl && (
        <div className="syw-viewer" onClick={() => setViewerUrl(null)} role="dialog" aria-label="Photo viewer">
          <img src={viewerUrl} alt="Full size" />
          <button className="syw-viewer-close" aria-label="Close">×</button>
        </div>
      )}
    </div>
  );
}
